import React from "react";
import HeroSection from "../common/HeroSection";
import {
  FileSearch,
  ClipboardList,
  Lightbulb,
  SlidersHorizontal,
  CircleCheckBig,
} from "lucide-react";
import SapServices from "./SapServices";

export default function SuccessFactor() {
  return (
    <div>
      <HeroSection
        title="SAP - Success Factors"
        breadcrumb={[
          { name: "Services", url: "/services" },
          { name: "SAP Services", url: "/services/sap-services" },
          {
            name: "SAP - Success Factors",
            url: "/services/sap-services/sap-successfactors",
          },
        ]}
        background="/assets/images/about/bg.png"
      />

      <div className="bg-white">
        <div className="py-16 px-4 mx-auto container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 px-4">
            {/* Sidebar */}
            <SapServices />

            {/* Content */}
            <div className="lg:col-span-2">
              <div className="relative rounded-xl overflow-hidden mb-8">
                <img
                  alt="SAP - Success Factors"
                  src="/assets/images/services/sap-successfactor.jpg"
                  className="w-full h-auto object-cover rounded-xl"
                  loading="lazy"
                />
              </div>

              <div className="mt-10">
                <div className="mx-auto">
                  <div className="relative mt-5">
                    <p className="text-gray-700 text-xl mb-3 font-bold">
                      Empowering Human Capital Management with Intelligent HR
                      Solutions
                    </p>
                    <p className="text-justify">
                      At <strong>STISSA Technology LLP (TISSA Technology)</strong>, we deliver
                      end-to-end
                      <strong>SAP SuccessFactors Services</strong> to help
                      organizations streamline and optimize their HR operations
                      using SAP’s leading cloud-based HCM suite. From strategic
                      implementation to post-go-live support, our certified
                      consultants ensure seamless adoption, business alignment,
                      and value realization across the employee lifecycle.
                    </p>

                    <h2 className="text-lg font-semibold mt-6 mb-4 text-blue-700">
                      Our SuccessFactors Service Portfolio
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Implementation Services
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Full-cycle implementation of SuccessFactors modules
                            based on SAP Activate methodology.
                          </li>
                          <li>
                            Tailored configurations aligned to your business
                            processes and compliance needs.
                          </li>
                          <li>
                            Expertise in Employee Central, Recruiting,
                            Onboarding, Performance & Goals, Learning,
                            Compensation, and Succession Planning.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Migration & Integration
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Smooth migration from legacy HR systems or SAP HCM
                            to SAP SuccessFactors.
                          </li>
                          <li>
                            Integration with SAP S/4HANA, SAP BTP, third-party
                            payroll, finance, and time systems using SAP CPI,
                            Dell Boomi, or MuleSoft.
                          </li>
                          <li>
                            Real-time sync via OData, SFAPI, Integration Center,
                            and custom middleware.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Customization & Extensions
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Extension of SuccessFactors functionality using SAP
                            BTP, UI5/Fiori apps, and MDK-based mobile apps.
                          </li>
                          <li>
                            Custom workflows, business rules, and role-based
                            permissions to meet enterprise-specific scenarios.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Post-Go-Live Support & Optimization
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Ongoing support for issue resolution, enhancements,
                            and configuration adjustments.
                          </li>
                          <li>
                            Quarterly release management and regression testing
                            to keep your system up to date.
                          </li>
                          <li>
                            Continuous improvement and KPI tracking through
                            embedded analytics.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Analytics & Reporting
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            Setup of People Analytics, custom dashboards, and
                            real-time reports.
                          </li>
                          <li>
                            Integration with SAP Analytics Cloud (SAC) for
                            advanced HR analytics, workforce planning, and
                            executive insights.
                          </li>
                        </ul>
                      </div>

                      <div className="border rounded p-4 bg-gray-50">
                        <h3 className="font-semibold mb-2">
                          Advisory & Change Management
                        </h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          <li>
                            HR process assessments and SuccessFactors readiness
                            evaluation.
                          </li>
                          <li>
                            Change management, end-user training, and adoption
                            enablement services.
                          </li>
                          <li>
                            Global rollout planning with compliance mapping for
                            localizations.
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h2 className="text-lg text-blue-600 font-semibold mb-4">
                        Why Choose TISSA Technology?
                      </h2>
                      <div className="grid grid-cols-1 gap-4 bg-gray-50 border p-6 rounded-md">
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Certified SAP SuccessFactors consultants with
                            cross-module expertise
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Strong experience in integration, extension, and
                            customization
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Successful implementations for mid-size to
                            enterprise clients across multiple regions
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Flexible engagement models — fixed-scope, T&M, or
                            managed services
                          </p>
                        </div>
                        <div className="flex items-start gap-4">
                          <span className="text-green-600 text-xl">✔</span>
                          <p>
                            Deep understanding of HR domain + technical
                            capability, including SAP BTP and Integration Suite
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h2 className="text-lg text-blue-600 font-semibold mb-4">
                        SuccessFactors Modules We Support
                      </h2>
                      <div className="bg-gray-50 grid grid-cols-1 gap-4 lg:grid-cols-2 p-6 rounded-xl border-l-4 border-primary">
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>Employee Central (Core HR)</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>Recruiting & Onboarding</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>Performance & Goals</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>Learning Management System (LMS)</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>Compensation & Variable Pay</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>Succession & Development</span>
                        </div>
                        <div className="flex items-start space-x-2">
                          <CircleCheckBig className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
                          <span>People Analytics (Embedded & SAC)</span>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mt-6">
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">
                          Digitize your workforce with SAP SuccessFactors and
                          TISSA’s expert services
                        </h3>
                        <p className="text-gray-700 mb-4">
                          👉{" "}
                          <a
                            className="text-blue-700 font-bold ms-2 hover:underline"
                            href="/contact-us/"
                          >
                            Contact Us
                          </a>{" "}
                          today to start your intelligent HR transformation
                          journey.
                        </p>
                        <div className="flex flex-wrap gap-4"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* End content */}
            </div>
          </div>
        </div>
      </div>

      {/* <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  What is included in your Odoo consultation service?
                </h3>
                <p className="text-gray-600">
                  Our consultation covers in-depth business process analysis,
                  evaluation of your current IT infrastructure, gap
                  identification, module recommendations, and creation of a
                  detailed implementation roadmap tailored to your goals and
                  workflows.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How do you determine if Odoo is a good fit for our business?
                </h3>
                <p className="text-gray-600">
                  We assess your operational needs, current software ecosystem,
                  and future goals. Based on this analysis, we recommend whether
                  Odoo is a suitable ERP solution, and if so, which modules
                  align best with your business functions.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  How long does the consultation process take?
                </h3>
                <p className="text-gray-600">
                  The duration depends on your business size and complexity. A
                  typical consultation project takes 1–3 weeks, including
                  discovery sessions, documentation review, and delivery of a
                  custom recommendation report or roadmap.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Will you provide a report or roadmap after consultation?
                </h3>
                <p className="text-gray-600">
                  Yes. At the end of the consultation, we provide a detailed
                  report outlining our findings, key improvement areas,
                  recommended Odoo modules, and a phase-wise implementation
                  strategy.
                </p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  Can you help with implementation after the consultation?
                </h3>
                <p className="text-gray-600">
                  Absolutely. Once the consultation is complete, our
                  implementation team can take over to execute the
                  roadmap—including module setup, customization, user training,
                  and ongoing support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
}
